/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: useAiProvidersList.ts
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Hook for fetching AI providers list data.
    Supports Domain Contextual Filtering pattern.

 🔁 Last Updated: 2025-01-03 by Rodrigo Cuadra (Added Domain Filtering)
────────────────────────────────────────────────────────────────*/

import { useCallback, useEffect } from 'react';
import { useNotifications } from '../../../contexts/NotificationContext';
import { useI18n } from '../../../hooks/useI18n';
import { useContextStore } from '../../../store/context';
import { getAiProviders } from '../api/aiProvidersApi';
import type { DataGridFetchResult } from '../../../hooks/useDataGrid';

interface UseAiProvidersListProps {
  domainId?: number;
  onRefreshNeeded?: () => void;
}

export function useAiProvidersList(props: UseAiProvidersListProps = {}) {
  const { showError } = useNotifications();
  const { t } = useI18n();
  const { selectedDomain } = useContextStore();

  // Use provided domainId or fallback to selected domain
  const effectiveDomainId = props.domainId ?? selectedDomain?.id;

  const fetchProviders = useCallback(async (params: {
    page: number;
    limit: number;
    sortBy: string;
    sortOrder: 'asc' | 'desc';
    search?: string;
  }) => {
    try {
      const response = await getAiProviders({
        ...params,
        tenantSlug: undefined,
        userId: undefined,
        domainId: effectiveDomainId
      });

      // Filter out any undefined or null items
      if (Array.isArray(response.data)) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        response.data = response.data.filter((provider: any) =>
          provider && typeof provider === 'object' && provider.id
        );
      }

      if (!response || typeof response !== 'object') {
        throw new Error('Invalid response format');
      }

      if (!Array.isArray(response.data)) {
        response.data = [];
      }

      if (!response.pagination) {
        response.pagination = {
          page: 1,
          limit: 10,
          total: 0,
          totalPages: 0
        };
      }

      return {
        data: response.data || [],
        pagination: response.pagination || {
          page: params.page,
          limit: params.limit,
          total: 0,
          totalPages: 0
        }
      } as DataGridFetchResult;
    } catch (error) {
      showError(t('aiProviders.messages.loadError'));
      throw error;
    }
  }, [effectiveDomainId, showError, t]);

  // Trigger refresh when domainId changes
  useEffect(() => {
    if (effectiveDomainId !== undefined && props.onRefreshNeeded) {
      props.onRefreshNeeded();
    }
  }, [effectiveDomainId]);

  return { fetchProviders, effectiveDomainId };
}

